import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';

import { ISchedule } from '../../../AppSlice';
import { endpoints, GatewayIp } from '../../../data/endpoints';

const PREVIEW_POLL_INTERVAL_MS = 1000;

const isRunningMediaEngineRecording = (schedule: ISchedule, now: moment.Moment): boolean => {
  if (schedule.taskType !== 'MEDIAENGINE_RECORD' || schedule.enabled === false || !schedule.slotId) {
    return false;
  }

  if (
    schedule.status === 0 ||
    schedule.status === '0' ||
    (typeof schedule.status === 'string' && ['paused', 'disabled', 'inactive'].includes(schedule.status.toLowerCase()))
  ) {
    return false;
  }

  const start = moment(schedule.startAt);
  const end = schedule.endAt ? moment(schedule.endAt) : null;

  return start.isValid() && start.isSameOrBefore(now) && (!end || (end.isValid() && end.isAfter(now)));
};

/** Polls authenticated still previews only for slots with an ongoing recording. */
export const useScheduledRecordingPreviews = (
  schedules: ISchedule[],
  token: string | null
): Record<string, string> => {
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const objectUrlsRef = useRef<Record<string, string>>({});

  useEffect(() => () => {
    Object.values(objectUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = {};
  }, []);

  useEffect(() => {
    const objectUrls = objectUrlsRef.current;
    const controller = new AbortController();
    let timeoutId: number | undefined;
    let disposed = false;

    const publish = () => setPreviewUrls({ ...objectUrls });

    const removePreview = (slotId: string) => {
      const previousUrl = objectUrls[slotId];
      if (!previousUrl) return false;
      URL.revokeObjectURL(previousUrl);
      delete objectUrls[slotId];
      return true;
    };

    const poll = async () => {
      const now = moment();
      const activeSlotIds = token
        ? Array.from(new Set(
            schedules
              .filter((schedule) => isRunningMediaEngineRecording(schedule, now))
              .map((schedule) => schedule.slotId as string)
          ))
        : [];
      const activeSlotIdSet = new Set(activeSlotIds);
      let changed = false;

      Object.keys(objectUrls).forEach((slotId) => {
        if (!activeSlotIdSet.has(slotId)) changed = removePreview(slotId) || changed;
      });

      if (token) {
        await Promise.all(activeSlotIds.map(async (slotId) => {
          try {
            const response = await axios.get(
              GatewayIp + endpoints.scheduler.slotPreview(slotId),
              {
                withCredentials: true,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
                signal: controller.signal,
                validateStatus: (status) => status === 200 || status === 404,
              }
            );

            if (disposed) return;

            if (response.status === 404) {
              changed = removePreview(slotId) || changed;
              return;
            }

            const blob = response.data as Blob;
            if (!blob?.size) return;

            const nextUrl = URL.createObjectURL(blob);
            removePreview(slotId);
            objectUrls[slotId] = nextUrl;
            changed = true;
          } catch (error) {
            if (!axios.isCancel(error)) {
              // Keep the last good frame on transient network/server failures.
            }
          }
        }));
      }

      if (!disposed && changed) publish();
      if (!disposed) timeoutId = window.setTimeout(poll, PREVIEW_POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      disposed = true;
      controller.abort();
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [schedules, token]);

  return previewUrls;
};
